import { Box, Button, Text } from '@ltvco/refresh-lib/theme';
import { capitalizeString } from '@ltvco/refresh-lib/utils';
import { type ApiTransactionRow } from '@ltvco/refresh-lib/v2';

import { TransactionListItem } from './components';

function formatDateTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString();
}

export type TransactionsPaginationMeta = {
  page: number;
  totalPages: number;
  totalCount: number;
  prevPage?: number | null;
  nextPage?: number | null;
};

export function TransactionsTabPanel({
  isLoading,
  transactions,
  meta,
  onPrevPage,
  onNextPage,
}: {
  isLoading: boolean;
  transactions: ApiTransactionRow[];
  meta: TransactionsPaginationMeta | undefined;
  onPrevPage: () => void;
  onNextPage: () => void;
}) {
  if (isLoading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Text variant="body2">No API transactions yet.</Text>
      </Box>
    );
  }

  const showPager = meta && meta.totalPages > 1;

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
        })}
      >
        {transactions.map((tx) => (
          <TransactionListItem key={tx.id}>
            <Text variant="body2" sx={{ flex: '1 1 160px' }}>
              {tx.endpoint}
            </Text>
            <Text variant="body2" fontWeight={500}>
              {tx.totalCredits} credits
            </Text>
            <Text variant="body2" color="text.secondary">
              {capitalizeString(tx.state)}
            </Text>
            <Text
              variant="caption"
              color="text.secondary"
              sx={{ flex: '1 1 100%', textAlign: { sm: 'right' } }}
            >
              {formatDateTime(String(tx.createdAt))}
            </Text>
          </TransactionListItem>
        ))}
      </Box>

      {showPager ? (
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            mt: 2,
          })}
        >
          <Button
            size="small"
            disabled={(meta.prevPage ?? null) === null}
            onClick={onPrevPage}
          >
            Previous
          </Button>
          <Text variant="caption">
            Page {meta.page} of {meta.totalPages} ({meta.totalCount} total)
          </Text>
          <Button
            size="small"
            disabled={(meta.nextPage ?? null) === null}
            onClick={onNextPage}
          >
            Next
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
